import { isUserEndedSessionTurnErrorText } from '@buildautomaton/types';
import type { BridgeToApiPromptResultMessage } from '../../../types/bridge-to-api-messages.js';
import type { LogFn } from '../../../log.js';
import { collectTurnGitDiffFromPreTurnSnapshot } from '../../../git/snapshot/diff/collect.js';
import { augmentPromptResultAuthFields } from '../send-prompt-result-augment.js';
import { maybeSubmitPlanningTodosAfterAgentSuccess } from '../../planning/maybe-submit-planning-todos-after-agent-success.js';
import { consumePlanningSessionTurnOutput } from '../../planning/planning-session-turn-buffer.js';

export async function finalizeAndSendPromptResult(params: {
  result: { success?: boolean; error?: unknown; output?: unknown; stopReason?: string };
  sessionId?: string;
  runId?: string;
  promptId?: string;
  agentType?: string | null;
  agentCwd?: string;
  isPlanningSession: boolean;
  followUpCatalogPromptId?: string | null;
  cloudApiBaseUrl?: string;
  getCloudAccessToken?: () => string;
  sendResult: (result: BridgeToApiPromptResultMessage) => void;
  sendSessionUpdate?: (payload: unknown) => void;
  log: LogFn;
}): Promise<void> {
  const {
    result,
    sessionId,
    runId,
    promptId,
    agentType,
    agentCwd,
    isPlanningSession,
    followUpCatalogPromptId,
    cloudApiBaseUrl,
    getCloudAccessToken,
    sendResult,
    sendSessionUpdate,
    log,
  } = params;

  if (sessionId && runId && sendSessionUpdate && agentCwd && result.success) {
    await collectTurnGitDiffFromPreTurnSnapshot({
      sessionId,
      runId,
      agentCwd,
      sendSessionUpdate,
      log,
    });
  }

  const planningTodosSubmit = await maybeSubmitPlanningTodosAfterAgentSuccess({
    sessionId,
    runId,
    resultSuccess: result.success === true,
    output: result.output,
    cloudApiBaseUrl,
    getCloudAccessToken,
    log,
  });

  const errStr = typeof result.error === 'string' ? result.error : undefined;
  const resultStop =
    typeof result.stopReason === 'string' ? result.stopReason.trim() : '';
  const cancelledByAgent =
    resultStop.toLowerCase() === 'cancelled' || (errStr != null && isUserEndedSessionTurnErrorText(errStr));
  const planningFallbackOutput =
    !planningTodosSubmit.suppressOutput && isPlanningSession && runId
      ? consumePlanningSessionTurnOutput(runId)
      : '';

  sendResult({
    type: 'prompt_result',
    id: promptId,
    ...(sessionId ? { sessionId } : {}),
    ...(runId ? { runId } : {}),
    ...result,
    ...(planningTodosSubmit.suppressOutput
      ? { output: undefined }
      : planningFallbackOutput.trim() !== ''
        ? { output: planningFallbackOutput }
        : {}),
    ...(followUpCatalogPromptId != null && followUpCatalogPromptId !== ''
      ? { followUpCatalogPromptId }
      : {}),
    ...augmentPromptResultAuthFields(agentType, errStr),
    ...(!result.success && cancelledByAgent ? { stopReason: 'cancelled' as const } : {}),
  });

  if (!result.success) {
    if (cancelledByAgent) {
      log('[Agent] Run ended after stop request (stopped by user).');
    } else {
      log(
        `[Agent] Prompt did not run successfully on the agent (no successful start/completion): ${result.error ?? 'Unknown error'}`,
      );
    }
  }
}
